import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  CanMatch,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree,
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { APP_MODEL_DATA } from '../model-data/app-model-data';
import { MainService } from '../services/main.service';
import { UtilService } from '../services/util.service';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate, CanActivateChild, CanMatch {
  constructor(
    private router: Router,
    private mainService: MainService,
    private utilService: UtilService
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    return this.checkAccess(state.url || '/');
  }

  canActivateChild(
    childRoute: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    return this.checkAccess(state.url || '/');
  }

  canMatch(route: Route, segments: UrlSegment[]): Observable<boolean | UrlTree> {
    const targetUrl = `/${segments.map((segment) => segment.path).join('/')}`;
    return this.checkAccess(targetUrl || '/');
  }

  private checkAccess(targetUrl: string): Observable<boolean | UrlTree> {
    const idToken = this.utilService.getToken('idToken');
    if (!idToken) {
      return of(this.router.parseUrl('/sign-in'));
    }

    let parsedToken: any;
    try {
      parsedToken = this.utilService.parseJwt(idToken);
    } catch (error) {
      return of(this.router.parseUrl('/sign-in'));
    }

    const userId = parsedToken?.user_id;
    if (!userId) {
      return of(this.router.parseUrl('/sign-in'));
    }

    return this.mainService.getUserAccountInfo(userId).pipe(
      map((userAccountInfo: any) => {
        const role = (userAccountInfo?.role || '').toString().toLowerCase();

        this.utilService.setLocalStorage('userRole', role);

        if (role === APP_MODEL_DATA.INTERNAL) {
          // Internal users can only access the restricted screen.
          if (targetUrl.startsWith('/access-restricted')) {
            return true;
          }
          return this.router.parseUrl('/access-restricted');
        }

        if (targetUrl.startsWith('/access-restricted')) {
          return this.router.parseUrl('/support-users/all');
        }

        return true;
      }),
      catchError(() => of(this.router.parseUrl('/sign-in')))
    );
  }
}
